import React from 'react'
import { Avatar } from 'antd'
import { QueueStatusEnum } from 'AppConstant'
import {
  LeftSection,
  CardImage,
  CardImageContent,
  KiroLogo,
  AgeBox,
  Text,
  SourceIdentifier,
  NameText,
  LastNameText,
  MetricRow,
  MetricButton,
  ScanBox,
  DateBox,
  PersonImage,
  UndoButton,
  StartButton,
  AppointmentsWrapper,
  NextButton,
  DoneButton,
} from './index.styled'
import usePatientProfile from './usePatientProfile'
import AppointmentCard from '../../../../components/AppointmentCard/AppointmentCard'
import DefaultAvatar from '../../../../images/ghostV2.svg'
import LogoBadgeOrange from '../../../../images/kiro-Logo-Orange.svg'
import LogoBadge from '../../../../images/LogoBadgeNew.png'
import LogoBadgeRed from '../../../../images/LogoBadgeRed.png'
import LogoBadgeYellow from '../../../../images/LogoBadgeYellow.png'
import { useAppSelector } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'
import { calculateAge } from '../../../../utils/Dob'
import { formatDollars } from '../../../../utils/FormatDollars'
import { isMember } from '../../../../utils/isMember'
import { getLeadSourceLabel } from '../../../../utils/LeadSource'
import { shouldShowBday, birthdayIconColor, birthdayIconTextColor } from '../../../../utils/ShouldShowBday'

export default function PatientProfilePanel() {
  const {
    currentPatient,
    currentVisit,
    formattedDate,
    queue,
    sessionStatus,
    handleCallInNextVisit,
    handleStartOrStopSession,
    sessionTime,
    colorInfo,
    handleUndoVisit,
    filteredAppointments,
    priorityVisit,
    stopChiropracticStatus,
    handlePriorityClick,
    handleDone,
  } = usePatientProfile()

  const member = isMember(currentPatient?.userPaymentPlan?.subscriptionExpiresAt || null)
  const firstPaid = formatDollars(currentPatient?.firstPaymentAmount)

  return (
    <LeftSection>
      {currentVisit && currentPatient && (
        <>
          <CardImage $src={currentPatient?.patient?.user?.profilePic || DefaultAvatar}>
            <CardImageContent>
              {(member || currentPatient?.isPaused) && (
                <KiroLogo
                  src={
                    currentPatient?.isPaused
                      ? LogoBadgeOrange
                      : currentPatient?.userPaymentPlan?.membershipStatus === 'in-active'
                        ? LogoBadgeRed
                        : LogoBadge
                  }
                  alt={currentPatient?.isPaused ? 'Membership paused' : 'Kiro'}
                />
              )}
              {currentPatient?.isPaymentPastDue && <KiroLogo src={LogoBadgeYellow} />}
              <AgeBox>
                <Text $bold>{calculateAge(currentPatient?.patient?.user?.dob)}</Text>
              </AgeBox>
            </CardImageContent>
            {/* Members already converted — the acquisition source only matters for non-members.
                A paused member is still a member even though the subscription may read expired. */}
            {!member && !currentPatient?.isPaused && (
              <SourceIdentifier $noBorder $spread>
                <Text $bold style={{ textTransform: 'capitalize' }}>
                  {getLeadSourceLabel(currentPatient?.lead)}
                </Text>
                {/* "$0" for free-flow patients who never had a payment row */}
                <Text $bold>{firstPaid}</Text>
              </SourceIdentifier>
            )}
          </CardImage>

          <NameText>{currentPatient?.patient?.user?.firstname?.toUpperCase() || ''}</NameText>
          <LastNameText>{currentPatient?.patient?.user?.lastname?.toUpperCase() || ''}</LastNameText>

          <MetricRow>
            <MetricButton
              style={{
                background:
                  !currentPatient?.feedback?.feedback && !currentPatient?.currentVisit?.isFirstBooking
                    ? '#4794EF'
                    : 'transparent',
              }}
            >
              <Text $bold $marginBottom={-8} $marginTop={-2}>
                REV
              </Text>
              <Text $size={30}>{currentPatient?.feedback?.feedback ?? '-'}</Text>
            </MetricButton>

            <MetricButton>
              <Text $bold $marginBottom={-8} $marginTop={-2}>
                REF
              </Text>
              <Text $size={30}>{currentPatient?.totalReferredPatients ?? '-'}</Text>
            </MetricButton>

            {currentPatient?.currentVisit?.isScanBooking && (
              <ScanBox>
                <Text $size={30}>SCAN</Text>
              </ScanBox>
            )}
          </MetricRow>

          {currentPatient?.patient?.user?.dob &&
          shouldShowBday(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) ? (
            <DateBox
              style={{
                background: birthdayIconColor(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) || '',
              }}
            >
              <Text
                $bold
                $color={birthdayIconTextColor(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) || ''}
              >
                {formattedDate.dayName}
              </Text>
              <Text
                $bold
                $color={birthdayIconTextColor(new Date(currentPatient?.patient?.user?.dob.slice(0, -1))) || ''}
              >
                {formattedDate.monthDay}
              </Text>
            </DateBox>
          ) : null}

          <AppointmentsWrapper>
            {filteredAppointments.map((v: any) => {
              const isPriorityVisit = priorityVisit?.id === v.visitId

              return (
                <AppointmentCard
                  key={v.visitId}
                  name={v?.visit?.patient?.user?.firstname + ' ' + v?.visit?.patient?.user?.lastname}
                  avatar={v?.visit?.patient?.user?.profilePic}
                  cardStyle={{
                    marginBottom: '8px',
                    outline: isPriorityVisit ? '1px solid #000000' : 'none',
                    outlineOffset: '-1px',
                  }}
                  nameStyle={{ fontWeight: 'normal' }}
                  onClick={() => handlePriorityClick(v)}
                />
              )
            })}
          </AppointmentsWrapper>
          {currentVisit &&
            currentPatient?.currentVisit?.status === QueueStatusEnum.CALLED_IN &&
            sessionStatus === QueueStatusEnum.WAITING && (
              <UndoButton onClick={() => handleUndoVisit()}>
                <Text $size={20} $bold>
                  Undo
                </Text>
              </UndoButton>
            )}
          {sessionStatus === QueueStatusEnum.COMPLETED && queue?.checkedIn?.length > 0 && (
            <DoneButton onClick={() => handleDone()}>
              <Text $size={20} $bold $color="var(--color-text-primary)">
                Done
              </Text>
            </DoneButton>
          )}
          <StartButton
            onClick={() => handleStartOrStopSession()}
            style={{ background: colorInfo.backgroundColor, border: colorInfo.border, color: colorInfo.color }}
          >
            <Text $size={20} $bold $color={colorInfo.color}>
              {/* {sessionStatus === QueueStatusEnum.WAITING && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                    {startChiropracticStatus !== APIStatus.pending && sessionTime !== '00:00' && (
                      <div>{sessionTime}</div>
                    )}
                    Start
                  </div>
                </>
              )} */}
              {currentPatient?.currentVisit?.status === QueueStatusEnum.CALLED_IN &&
                sessionStatus === QueueStatusEnum.WAITING && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                      Start
                    </div>
                  </>
                )}
              {sessionStatus === QueueStatusEnum.SERVICE_IN_PROGRESS && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                    {stopChiropracticStatus !== APIStatus.pending && <div>{sessionTime}</div>}
                  </div>
                </>
              )}
              {sessionStatus === QueueStatusEnum.COMPLETED && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                    {currentVisit && queue?.checkedIn?.length > 0 ? 'Next' : 'Done'}
                  </div>
                </>
              )}
            </Text>
          </StartButton>
        </>
      )}
      {!currentVisit && queue.checkedIn.length > 0 && (
        <NextButton onClick={() => handleCallInNextVisit()}>
          <Text $size={20} $bold $color="var(--color-text-secondary)">
            Next
          </Text>
        </NextButton>
      )}
      {!currentPatient && currentVisit && queue.checkedIn.length > 0 && (
        <NextButton onClick={() => handleCallInNextVisit()}>
          <Text $size={20} $bold $color="var(--color-text-secondary)">
            Next
          </Text>
        </NextButton>
      )}
    </LeftSection>
  )
}
